/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useRef, useState } from "react";
import { NetworkStatus } from "@apollo/client";
import dayjs from "dayjs";
import {
  ReportStatus,
  ResourceComplianceStatusInfo,
  useResourceComplianceStatusForUiQuery,
} from "~/generated/graphql";

export function useResourceComplianceStatusForUI({
  resourceIds,
  skip,
}: {
  resourceIds: string[];
  skip?: boolean;
}) {
  const POLL_INTERVAL_MS = 2000;
  const MAX_ATTEMPTS = 15;
  const [attempt, setAttempt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startDate = dayjs().startOf("month").toISOString();
  const endDate = dayjs().endOf("month").toISOString();

  const { data, loading, error, refetch, networkStatus } =
    useResourceComplianceStatusForUiQuery({
      variables: {
        input: {
          resourceIds: resourceIds.length ? resourceIds : undefined,
          dateRange: { startDate, endDate },
        },
      },
      skip: skip || resourceIds.length === 0,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    });

  const status = data?.resourceComplianceStatusForUI?.status;

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (status === ReportStatus.InProgress) {
      if (attempt >= MAX_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      timerRef.current = setTimeout(() => {
        setAttempt((prev) => prev + 1);
        refetch();
      }, POLL_INTERVAL_MS);
    } else {
      setTimedOut(false);
      setAttempt(0);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [status, attempt, refetch]);

  const retry = () => {
    setTimedOut(false);
    setAttempt(0);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    refetch();
  };

  const complianceStatuses: ResourceComplianceStatusInfo[] =
    data?.resourceComplianceStatusForUI?.complianceStatuses ?? [];

  const isCompleted = status === ReportStatus.Completed;
  const isFailed = status === ReportStatus.Failed;
  const isPolling = status === ReportStatus.InProgress && !timedOut;
  const isRefetching = networkStatus === NetworkStatus.refetch;
  const isInitialLoad = loading && !data;
  const isLoading = isInitialLoad || isPolling || isRefetching;

  return {
    data: complianceStatuses,
    status,
    loading: isLoading,
    error,
    isPolling,
    timedOut,
    isCompleted,
    isFailed,
    retry,
  };
}
